import { useState } from 'react'
import { Alert, Badge, Button, Drawer, Group, Stack, Text } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { BASE_MEU_ROBO, type Robo } from '@/api/robo'
import { useAuthStore } from '@/store/auth'
import { RoboAlertTable } from './RoboAlertTable'
import { RoboStatus } from './RoboStatus'
import { useEvidenciaDoRobo } from './useEvidenciaDoRobo'

/**
 * O que a conversa com o robô tem a mais que as outras (spec 2026-10-02): uma
 * linha de estado abaixo do cabeçalho — conexão do aparelho, frescor da coleta,
 * escopo — e a gaveta de alertas, com o "Mostrar em <robô>" de sempre.
 */
export function RoboDmTopo({ robo, cid }: { robo: Robo; cid: number }) {
  const userId = useAuthStore((s) => s.user?.id)
  const [aberta, setAberta] = useState(false)
  const celular = useMediaQuery('(max-width: 48em)')
  const { evidencia, fresh, negado, atualizar, atualizando } = useEvidenciaDoRobo(robo, true)
  const escopo = robo.tenant_name ?? `tenant ${robo.tenant_id}`
  return (
    <>
      <Group gap="xs" px="md" py={6} wrap="wrap"
        style={{ borderBottom: '1px solid var(--ai-border-light, var(--mantine-color-gray-2))', flexShrink: 0 }}>
        <RoboStatus base={BASE_MEU_ROBO} cid={cid} userId={userId} nome={robo.name} compacto />
        {negado
          ? <Badge color="orange" variant="light">Sem acesso aos alertas</Badge>
          : <Badge color={fresh ? 'teal' : 'orange'} variant="light">{fresh ? 'Coleta recente' : 'Sem confirmação recente'}</Badge>}
        <Text size="xs" c="dimmed">Somente leitura · {escopo}</Text>
        <Button size="compact-xs" variant="light" ml="auto" onClick={() => setAberta(true)}>
          Alertas{evidencia ? ` (${evidencia.active_count})` : ''}
        </Button>
      </Group>
      <Drawer opened={aberta} onClose={() => setAberta(false)} position="right"
        size={celular ? '100%' : 'xl'} title={`Alertas ativos · ${escopo}`}>
        {negado ? (
          <Alert color="orange">Este contato exige acesso de leitura aos alertas de {escopo}.</Alert>
        ) : (
          <Stack gap="sm">
            {!fresh && <Alert color="orange">
              Ainda não há confirmação recente. Isso não significa que os alertas foram resolvidos.
              {evidencia && ' A tabela abaixo é a última coleta disponível.'}
            </Alert>}
            <Group justify="space-between">
              {evidencia
                ? <Text size="xs" c="dimmed">Coleta: {new Date(evidencia.collected_at * 1000).toLocaleString('pt-BR')}</Text>
                : <span />}
              <Button variant="subtle" size="xs" loading={atualizando} onClick={atualizar}>Atualizar</Button>
            </Group>
            <RoboAlertTable key={`${userId}:${robo.public_id}:${cid}`} rows={evidencia?.alerts ?? []}
              fresh={fresh} base={BASE_MEU_ROBO} cid={cid} userId={userId} nome={robo.name} />
            {fresh && evidencia?.active_count === 0 && <Text>Nenhum problema ativo na coleta completa mais recente.</Text>}
          </Stack>
        )}
      </Drawer>
    </>
  )
}
